import React from "react";
import { Clock } from "lucide-react";
import { useCountdown } from "@/hooks/useCountdown";

/**
 * Live HH:MM:SS countdown strip, absolutely-positioned along the bottom of a
 * poster. Renders nothing when `validTo` is missing/invalid/past.
 *
 * Style mirrors the mobile app's urgency-block palette
 * (`/app/mobile/app/posts/[id].tsx` → `urgencyBlockRed` / `urgencyTextRed`
 * `#B91C1C`, and `#991B1B` for the critical <1h tier) but rendered as a
 * bottom overlay strip on the poster with red bold text on a semi-transparent
 * dark background so it stays legible over any hero image.
 *
 * Requires the poster wrapper to have `position: relative` (usual for a
 * `<div class="relative aspect-[4/3]">`).
 */
export function CountdownStrip({ validTo, size = "md", testid }) {
    const c = useCountdown(validTo);
    if (!validTo || c.expired) return null;

    const isCritical = c.totalMs > 0 && c.totalMs < 3600 * 1000; // <1h
    const isEndingSoon = c.isEndingSoon; // <48h

    // Size ramps: `sm` for small tiles (aspect square), `md` default for
    // 4:3 cards, `lg` reserved for hero.
    const sizes = {
        sm: "px-2 py-1 text-[10px] gap-1",
        md: "px-3 py-1.5 text-xs gap-1.5",
        lg: "px-3.5 py-2 text-sm gap-2",
    };
    const iconSizes = { sm: 11, md: 13, lg: 15 };
    const pad = sizes[size] || sizes.md;

    const textColor = isCritical
        ? "text-red-400" // <1h — brightest red, matches mobile "critical" tier
        : isEndingSoon
            ? "text-red-500" // <48h
            : "text-white";

    return (
        <div
            data-testid={testid || "offer-countdown-strip"}
            className={`absolute inset-x-0 bottom-0 flex items-center justify-center bg-black/70 font-mono font-bold backdrop-blur-sm ${pad} ${textColor} ${isCritical ? "animate-pulse" : ""}`}
            aria-live="off"
        >
            <Clock size={iconSizes[size] || 13} className={textColor} strokeWidth={2.5} />
            <span className="tabular-nums tracking-wider">{c.hhmmss}</span>
        </div>
    );
}

/**
 * "Ending Soon" badge for offers ending within 48h. Absolutely-positioned to
 * top-right of the poster by default. Renders nothing outside the 48h window
 * or when the offer has expired.
 *
 * The threshold logic (`< 48h` remaining) matches the mobile urgency-block
 * "hours" / "urgent" tiers (`totalHrs < 24` → red styling).
 */
/**
 * "Ending Soon" badge for offers ending within 48h. Layout-agnostic — the
 * caller places it (either inside an absolute stack or standalone). Renders
 * nothing outside the 48h window or when the offer has expired.
 *
 * The threshold logic (`< 48h` remaining) matches the mobile urgency-block
 * "hours" / "urgent" tiers (`totalHrs < 24` → red styling).
 */
export function EndingSoonBadge({ validTo, className = "", testid }) {
    // Hooks must always be called unconditionally. `useCountdown` handles
    // the null-`validTo` case internally (returns EMPTY and never subscribes).
    const c = useCountdown(validTo);
    if (!validTo || c.expired || !c.isEndingSoon) return null;

    return (
        <span
            data-testid={testid || "ending-soon-badge"}
            className={`inline-flex items-center gap-1 rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-white shadow-md ring-1 ring-red-700/40 ${className}`}
        >
            <span aria-hidden>⏰</span> Ending Soon
        </span>
    );
}

export default CountdownStrip;
